import { Card } from 'src/design/atoms/Card';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { ChangeTypeDto } from 'src/api/FiretrolApiClient';
import {
  useCreateChangeType,
  useUpdateChangeType,
} from 'src/api/queries/changeTypes/useChangeTypeMutations';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(1, 'Name is required').label('Name'),
  sortOrder: Yup.number().required().min(0).label('Sort Order'),
  isActive: Yup.boolean(),
});

const defaultValues = {
  name: '',
  isActive: true,
  sortOrder: 0,
};

interface ChangeTypeFormProps {
  initialValues?: ChangeTypeDto;
}

export const ChangeTypeForm: React.FC<ChangeTypeFormProps> = ({
  initialValues,
}) => {
  const navigate = useNavigate();
  const isEditing = !!initialValues?.id;
  const createChangeType = useCreateChangeType();
  const updateChangeType = useUpdateChangeType();

  const formik = useFormik({
    initialValues: initialValues
      ? {
          name: initialValues.name,
          isActive: initialValues.isActive,
          sortOrder: initialValues.sortOrder,
        }
      : defaultValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { resetForm }) => {
      if (isEditing && initialValues) {
        const result = await updateChangeType
          .mutateAsync({ id: initialValues.id, request: values })
          .catch(() => null);
        if (!result) return;
        resetForm({
          values: {
            name: result.name,
            isActive: result.isActive,
            sortOrder: result.sortOrder,
          },
        });
      } else {
        const result = await createChangeType
          .mutateAsync(values)
          .catch(() => null);
        if (result) navigate(`/settings/change-types/edit/${result.id}`);
      }
    },
  });

  const { values, isValid, dirty, setFieldValue } = formik;
  const isSaving = createChangeType.isPending || updateChangeType.isPending;

  return (
    <>
      <Group justify="flex-end" mb="md">
        <SecondaryButton onClick={() => navigate('/settings/change-types')}>
          Cancel
        </SecondaryButton>
      </Group>
      <Card>
        <Stack maw={500}>
          <TextInput
            label="Name"
            placeholder="What do you want to call this change type?"
            withAsterisk
            value={values.name}
            onChange={(e) => setFieldValue('name', e.target.value)}
            error={formik.touched.name && formik.errors.name}
          />
          <NumberInput
            label="Sort Order"
            description="Controls the order change types appear in the picker."
            min={0}
            value={values.sortOrder}
            onChange={(value) =>
              setFieldValue('sortOrder', typeof value === 'number' ? value : 0)
            }
            error={formik.touched.sortOrder && formik.errors.sortOrder}
          />
          <Checkbox
            label="Active"
            description="Inactive change types are hidden from the picker but remain on historic changes."
            checked={values.isActive}
            onChange={(e) => setFieldValue('isActive', e.target.checked)}
          />
        </Stack>
      </Card>

      <Group mt="md">
        {isEditing ? (
          <SaveButton
            onClick={() => formik.submitForm()}
            loading={isSaving}
            disabled={!isValid || !dirty}
          />
        ) : (
          <CreateButton
            onClick={() => formik.submitForm()}
            loading={isSaving}
            disabled={!isValid || !dirty}
          >
            Create Change Type
          </CreateButton>
        )}
      </Group>
    </>
  );
};
